/**
 * Types for the Component mapping run and its per-(component version, report source) state.
 * Mirrors the backend `ComponentReportMappingStateDTO`, `MappingRunStatus`, `MappingRunMode`.
 */

/** Lifecycle of one mapping-run run. */
export enum MappingRunStatus {
    QUEUED = 'QUEUED',
    RUNNING = 'RUNNING',
    COMPLETED = 'COMPLETED',
    ERROR = 'ERROR',
    CANCELLED = 'CANCELLED',
}

/** How a mapping run chooses the reports it evaluates. */
export enum MappingRunMode {
    /** Evaluate the reports with no current record for this component version and source. */
    UPDATE = 'UPDATE',
    /** Discard every record for the pair and evaluate the whole source again. */
    FULL_REMAP = 'FULL_REMAP',
    /** For a draft: evaluate at most the given number of reports. */
    TEST = 'TEST',
}

/** Where a component version stands against a source, derived from the records on every read. */
export enum ComponentMappingStatus {
    MAPPING_IN_PROGRESS = 'MAPPING_IN_PROGRESS',
    NOT_MAPPED = 'NOT_MAPPED',
    PARTIALLY_MAPPED = 'PARTIALLY_MAPPED',
    CHANGED_SINCE_MAPPING = 'CHANGED_SINCE_MAPPING',
    MAPPED = 'MAPPED',
}

/**
 * The derived status and the counts behind it (backend `ComponentMappingStatusDTO`). The four
 * report counts always add up to `reportsInSource`.
 */
export interface ComponentMappingStatusSummary {
    componentId: number;
    sourceName: string;
    status: ComponentMappingStatus;
    reportsInSource: number;
    withCurrentRecord: number;
    neverEvaluated: number;
    failedToEvaluate: number;
    changedSinceRecord: number;
    runStatus?: MappingRunStatus;
    lastRunAt?: string;
}

/** A run is active (still doing work) when it is queued or running. */
export const ACTIVE_RUN_STATUSES: readonly MappingRunStatus[] = [MappingRunStatus.QUEUED, MappingRunStatus.RUNNING];

export function isRunActive(status?: MappingRunStatus | null): boolean {
    return status != null && ACTIVE_RUN_STATUSES.includes(status);
}

/**
 * Live mapping-run progress snapshot (backend `ComponentMappingRunStatistics`).
 *
 * Every field is optional because this arrives as a schema-less JSONB document, not a typed DTO:
 * the current writer fills all of them, but a row written by an earlier shape of the snapshot does
 * not, and nothing in the round trip would reject it. Read each count through `toCount`.
 */
export interface ComponentMappingRunStatistics {
    totalReports?: number;
    processed?: number;
    mapped?: number;
    needsReview?: number;
    notApplicable?: number;
    failed?: number;
    nodeMappings?: number;
    percentComplete?: number;
    updatedAt?: string;
}

/** Per-(component version, report source) mapping state plus the latest run's live status. */
export interface ComponentReportMappingState {
    id: number;
    componentId: number;
    sourceName: string;
    lastRunAt?: string;
    runStatus?: MappingRunStatus;
    runMode?: MappingRunMode;
    runReportLimit?: number;
    runStartedAt?: string;
    runCompletedAt?: string;
    runError?: string;
    runStatistics?: ComponentMappingRunStatistics;
    runStatisticsUpdatedAt?: string;
}

export interface StartMappingRunOptions {
    mode?: MappingRunMode;
    limit?: number;
}
